import React, { useEffect, useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCreditsCheck } from '../hooks/useCreditsCheck';
import { useAIGenerationEnabled } from '../hooks/useAIGenerationEnabled';
import {
  PlusCircle,
  FileText,
  Settings,
  TrendingUp,
  LogOut,
  Clock,
  CheckCircle,
  Mail,
  Users,
  Share2,
  Eye,
  Menu,
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { MaintenanceBanner } from '../components/MaintenanceBanner';
import { LowCreditsBanner } from '../components/LowCreditsBanner';
import { Sidebar } from '../components/Sidebar';
import { MobileNav } from '../components/MobileNav';
import { DashboardSkeleton } from '../components/skeletons/DashboardSkeleton';
import { AanDeSlagRow } from '../components/aandeslag/AanDeSlagRow';
import { WalkthroughOverlay } from '../components/aandeslag/WalkthroughOverlay';
import { getBlogUrlConfigForUser, generateBlogUrl, type BlogUrlConfig } from '../lib/blogUrlHelper';
import { shouldShowWalkthrough } from '../lib/walkthroughGate';

interface RecentBlog {
  id: string;
  slug: string;
  title: string;
  published_at: string;
  view_count: number;
  featured_image_url: string | null;
  wp_post_url: string | null;
}

interface UserWithUsername {
  id: string;
  email: string;
  first_name: string;
  company_name: string;
  username_slug: string | null;
  blog_schedule_enabled: boolean;
  next_blog_scheduled_at: string | null;
}

interface RecentCampaign {
  id: string;
  subject: string;
  status: 'draft' | 'scheduled' | 'sending' | 'completed' | 'failed';
  scheduled_at: string | null;
  sent_at: string | null;
  sent_count: number;
  total_contacts: number;
}

interface RecentSocialPost {
  id: string;
  platform: string;
  post_content: string;
  status: string;
  scheduled_at: string | null;
  created_at: string;
}

export function Dashboard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading: authLoading, signOut, isAdmin, isSupportUser } = useAuth();
  const creditsInfo = useCreditsCheck(user?.id);
  const { isEnabled: aiGenerationEnabled } = useAIGenerationEnabled();
  const [recentBlogs, setRecentBlogs] = useState<RecentBlog[]>([]);
  const [stats, setStats] = useState({
    activeBlogs: 0,
    pendingCampaigns: 0,
    pendingSocialPosts: 0,
    viewsThisMonth: 0,
    viewsThisWeek: 0,
    emailSubscribers: 0,
  });
  const [recentCampaigns, setRecentCampaigns] = useState<RecentCampaign[]>([]);
  const [recentSocialPosts, setRecentSocialPosts] = useState<RecentSocialPost[]>([]);
  const [loadingData, setLoadingData] = useState(true);
  const [previewUser, setPreviewUser] = useState<UserWithUsername | null>(null);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [usernameSlug, setUsernameSlug] = useState<string | null>(null);
  const [blogUrlConfig, setBlogUrlConfig] = useState<BlogUrlConfig | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showWalkthrough, setShowWalkthrough] = useState(false);

  useEffect(() => {
    const previewUserId = searchParams.get('preview');
    console.log('[Preview] URL params - preview:', previewUserId);

    if (previewUserId) {
      console.log('[Preview] Entering preview mode for user:', previewUserId);
      setIsPreviewMode(true);
      loadPreviewUser(previewUserId);
    } else {
      setIsPreviewMode(false);
      if (!authLoading && !user) {
        navigate('/login');
      } else if (user) {
        loadDashboardData();
      }
    }
  }, [user, authLoading, navigate, searchParams]);

  useEffect(() => {
    if (authLoading || isPreviewMode || !user?.id) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('user_onboarding_progress')
        .select('completed, skipped, dashboard_walkthrough_seen_at')
        .eq('user_id', user.id)
        .maybeSingle();
      if (cancelled || !data) return;
      const show = shouldShowWalkthrough({
        isAdmin,
        isSupportUser,
        completed: !!data.completed,
        skipped: !!data.skipped,
        seenAt: data.dashboard_walkthrough_seen_at,
      });
      if (!show) return;
      setShowWalkthrough(true);
      const { error: seenError } = await supabase
        .from('user_onboarding_progress')
        .update({ dashboard_walkthrough_seen_at: new Date().toISOString() })
        .eq('user_id', user.id);
      if (seenError) console.error('Error marking walkthrough as seen:', seenError);
    })();
    return () => { cancelled = true; };
  }, [authLoading, isPreviewMode, user?.id, isAdmin, isSupportUser]);

  const loadPreviewUser = async (userId: string) => {
    try {
      setLoadingData(true);
      console.log('[Preview] Loading preview for user ID:', userId);

      // Use the public edge function to get user data (bypasses any client-side RLS issues)
      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-user-preview?userId=${userId}`;
      const response = await fetch(apiUrl, {
        headers: {
          'Authorization': `Bearer ${(import.meta.env.VITE_SUPABASE_ANON_KEY)}`,
        },
      });

      console.log('[Preview] API response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json();
        console.error('[Preview] API error:', errorData);
        setLoadingData(false);
        return;
      }

      const result = await response.json();
      const userData = result.data;

      console.log('[Preview] User data received:', userData);

      if (userData) {
        console.log('[Preview] User loaded successfully:', userData.company_name);
        setPreviewUser(userData);
        setUsernameSlug(userData.username_slug);
        await loadDashboardDataForUser(userId);
      } else {
        console.error('[Preview] No user data in response');
        setLoadingData(false);
      }
    } catch (error) {
      console.error('[Preview] Exception loading preview user:', error);
      setLoadingData(false);
    }
  };

  const loadDashboardData = async () => {
    if (!user) return;

    // Load username_slug for the authenticated user
    const { data: userData } = await supabase
      .from('users')
      .select('username_slug')
      .eq('id', user.id)
      .single();

    if (userData) {
      setUsernameSlug(userData.username_slug);
    }

    // Load blog URL config
    const urlConfig = await getBlogUrlConfigForUser(user.id, supabase);
    setBlogUrlConfig(urlConfig);

    loadDashboardDataForUser(user.id);
    cleanupOrphanSessions(user.id);
  };

  const cleanupOrphanSessions = async (userId: string) => {
    try {
      const fiveMinutesAgo = new Date();
      fiveMinutesAgo.setMinutes(fiveMinutesAgo.getMinutes() - 5);

      const { data: potentialOrphans } = await supabase
        .from('sessions')
        .select('id')
        .eq('user_id', userId)
        .eq('state', 'idea_selection')
        .lt('created_at', fiveMinutesAgo.toISOString());

      if (!potentialOrphans || potentialOrphans.length === 0) return;

      const orphanIds = potentialOrphans.map(s => s.id);
      const { data: ideasForOrphans } = await supabase
        .from('blog_ideas')
        .select('session_id')
        .in('session_id', orphanIds);

      const idsWithIdeas = new Set((ideasForOrphans || []).map(r => r.session_id));
      const toDelete = orphanIds.filter(id => !idsWithIdeas.has(id));

      if (toDelete.length > 0) {
        await supabase.from('sessions').delete().in('id', toDelete);
      }
    } catch (err) {
      console.error('Error cleaning up orphan sessions:', err);
    }
  };

  const loadDashboardDataForUser = async (userId: string) => {
    try {
      setLoadingData(true);

      // Load blog URL config
      const urlConfig = await getBlogUrlConfigForUser(userId, supabase);
      setBlogUrlConfig(urlConfig);

      // Calculate date ranges
      const now = new Date();
      const startOfWeek = new Date(now);
      startOfWeek.setDate(now.getDate() - 7);
      const startOfMonth = new Date(now);
      startOfMonth.setMonth(now.getMonth() - 1);

      // Load recent published blogs (only active ones)
      const { data: blogs, error: blogsError } = await supabase
        .from('published_blogs')
        .select('id, slug, title, published_at, view_count, featured_image_url, wp_post_url')
        .eq('user_id', userId)
        .eq('is_active', true)
        .is('deleted_at', null)
        .order('published_at', { ascending: false })
        .limit(3);

      if (blogsError) {
        console.error('Error loading blogs:', blogsError);
      } else if (blogs) {
        setRecentBlogs(blogs);
      }

      // Calculate stats - active blogs
      const { data: allBlogs, error: statsError } = await supabase
        .from('published_blogs')
        .select('is_active')
        .eq('user_id', userId)
        .is('deleted_at', null);

      if (statsError) {
        console.error('Error loading stats:', statsError);
      } else if (allBlogs) {
        const activeBlogs = allBlogs.filter(blog => blog.is_active).length;

        setStats(prev => ({
          ...prev,
          activeBlogs,
        }));
      }

      // Calculate views from widget_analytics - this week
      const { count: viewsThisWeek } = await supabase
        .from('widget_analytics')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId)
        .gte('created_at', startOfWeek.toISOString());

      // Calculate views from widget_analytics - this month
      const { count: viewsThisMonth } = await supabase
        .from('widget_analytics')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId)
        .gte('created_at', startOfMonth.toISOString());

      setStats(prev => ({
        ...prev,
        viewsThisWeek: viewsThisWeek || 0,
        viewsThisMonth: viewsThisMonth || 0,
      }));

      // Load email subscribers count
      const { count: subscribersCount } = await supabase
        .from('email_contacts')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId)
        .eq('subscribed', true);

      setStats(prev => ({
        ...prev,
        emailSubscribers: subscribersCount || 0,
      }));

      // Load recent campaigns
      const { data: campaigns, error: campaignsError } = await supabase
        .from('email_campaigns')
        .select('id, subject, status, scheduled_at, sent_at, sent_count, total_contacts')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(3);

      if (campaignsError) {
        console.error('Error loading campaigns:', campaignsError);
      } else if (campaigns) {
        setRecentCampaigns(campaigns);
      }

      // Load scheduled campaigns count (only scheduled)
      const { count: pendingCampaignsCount } = await supabase
        .from('email_campaigns')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId)
        .eq('status', 'scheduled');

      setStats(prev => ({
        ...prev,
        pendingCampaigns: pendingCampaignsCount || 0,
      }));

      // Load recent social media posts
      const { data: socialPosts, error: socialError } = await supabase
        .from('social_media_posts')
        .select('id, platform, post_content, status, scheduled_at, created_at')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(3);

      if (socialError) {
        console.error('Error loading social posts:', socialError);
      } else if (socialPosts) {
        setRecentSocialPosts(socialPosts);
      }

      // Load scheduled social posts count (only scheduled)
      const { count: pendingSocialCount } = await supabase
        .from('social_media_posts')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId)
        .eq('status', 'scheduled');

      setStats(prev => ({
        ...prev,
        pendingSocialPosts: pendingSocialCount || 0,
      }));

    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoadingData(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  if (isPreviewMode && !loadingData && !previewUser) {
    return (
      <div className="min-h-screen bg-[#F2F2EF] flex items-center justify-center">
        <div className="max-w-md text-center p-8">
          <p className="text-gray-600 mb-4">Gebruiker niet gevonden</p>
          <button
            onClick={() => window.close()}
            className="btn-secondary"
          >
            Sluiten
          </button>
        </div>
      </div>
    );
  }

  const isLoadingShell = isPreviewMode
    ? loadingData
    : (authLoading || loadingData || !user);

  const displayUser = isPreviewMode ? previewUser : user;
  const displayName = displayUser?.first_name || displayUser?.email || 'Gebruiker';

  return (
    <div className="min-h-screen bg-[#F2F2EF]">
      {!isPreviewMode && (
        <>
          <Sidebar onSignOut={handleSignOut} />
          <MobileNav
            isOpen={isMobileMenuOpen}
            onClose={() => setIsMobileMenuOpen(false)}
            onSignOut={handleSignOut}
          />
        </>
      )}

      <div className={!isPreviewMode ? 'lg:pl-64' : ''}>
        {/* Preview Banner */}
        {isPreviewMode && (
          <div className="bg-blue-600 text-white py-2 px-4">
            <div className="max-w-7xl mx-auto flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">Preview Modus</span>
                <span className="text-sm opacity-80">Je bekijkt het dashboard van {displayUser?.company_name}</span>
              </div>
              <button
                onClick={() => window.close()}
                className="text-sm underline hover:no-underline"
              >
                Sluiten
              </button>
            </div>
          </div>
        )}

        <MaintenanceBanner />
        {!isPreviewMode && <LowCreditsBanner />}

        {/* Header */}
        <header className="bg-white border-b border-gray-200 sticky top-0 z-40 lg:hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {!isPreviewMode && (
                  <button
                    onClick={() => setIsMobileMenuOpen(true)}
                    className="lg:hidden p-2 text-gray-700 hover:text-[#FF6B35] hover:bg-gray-100 rounded-lg transition-colors"
                    title="Menu"
                  >
                    <Menu className="w-6 h-6" />
                  </button>
                )}
                <img
                  src="https://cdn.prod.website-files.com/68106eab8acafbe1be69f17a/6830b7212ae8f51f0b5e545b_12ebc92fd52362293d48e2636ef26ed3_logo.svg"
                  alt="Dibbi Logo"
                  className="h-8 lg:hidden"
                />
              </div>
              {!isPreviewMode && (
                <div className="flex items-center gap-3">
                  <Link
                    to="/dashboard/settings"
                    className="lg:hidden p-2 text-gray-700 hover:text-[#FF6B35] hover:bg-gray-100 rounded-lg transition-colors"
                    title="Instellingen"
                  >
                    <Settings className="w-6 h-6" />
                  </Link>
                  <button
                    onClick={handleSignOut}
                    className="lg:hidden p-2 text-gray-700 hover:text-[#FF6B35] hover:bg-gray-100 rounded-lg transition-colors"
                    title="Uitloggen"
                  >
                    <LogOut className="w-6 h-6" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8 lg:pt-8">
        {!isPreviewMode && <AanDeSlagRow onShowWalkthrough={() => setShowWalkthrough(true)} />}
        {isLoadingShell ? <DashboardSkeleton /> : <>
        {/* 3 Hoofdingangen */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mb-8">
          {/* Blogs Card */}
          <Link
            to="/my-blogs"
            className="bg-white rounded-xl p-4 md:p-6 border-2 border-gray-300 shadow-sm hover:shadow-md transition-all group"
          >
            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 md:w-10 md:h-10 rounded-lg bg-gray-100 flex items-center justify-center text-gray-600">
                    <FileText className="w-4 h-4 md:w-5 md:h-5" />
                  </div>
                  <h3 className="font-roca text-base md:text-lg font-semibold text-[#231F20] group-hover:text-indigo-600 transition-colors">
                    Blogs
                  </h3>
                </div>
                {!isPreviewMode && (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      if (!creditsInfo.hasCredits || !aiGenerationEnabled) return;
                      navigate('/dashboard/new-blog');
                    }}
                    disabled={!creditsInfo.hasCredits || !aiGenerationEnabled}
                    title={!aiGenerationEnabled ? 'AI generatie is tijdelijk uitgeschakeld' : !creditsInfo.hasCredits ? 'Onvoldoende credits' : 'Nieuwe blog maken'}
                    className={`text-xs px-2.5 md:px-3 py-1 md:py-1.5 rounded-lg font-medium transition-colors ${
                      (!creditsInfo.hasCredits || !aiGenerationEnabled)
                        ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                        : 'bg-[#FF6B35] text-white hover:bg-[#FF5520]'
                    }`}
                  >
                    Nieuw +
                  </button>
                )}
              </div>
              <div className="text-center">
                <p className="text-base text-gray-700">{stats.activeBlogs} actieve blogs</p>
              </div>
            </div>
          </Link>

          {/* Campaigns Card */}
          <Link
            to="/campaigns"
            className="bg-white rounded-xl p-4 md:p-6 border-2 border-gray-300 shadow-sm hover:shadow-md transition-all group"
          >
            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 md:w-10 md:h-10 rounded-lg bg-gray-100 flex items-center justify-center text-gray-600">
                    <Mail className="w-4 h-4 md:w-5 md:h-5" />
                  </div>
                  <h3 className="font-roca text-base md:text-lg font-semibold text-[#231F20] group-hover:text-purple-600 transition-colors">
                    Campagnes
                  </h3>
                </div>
                {!isPreviewMode && (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      if (!creditsInfo.hasCredits || !aiGenerationEnabled) return;
                      navigate('/campaigns/create');
                    }}
                    disabled={!creditsInfo.hasCredits || !aiGenerationEnabled}
                    title={!aiGenerationEnabled ? 'AI generatie is tijdelijk uitgeschakeld' : !creditsInfo.hasCredits ? 'Onvoldoende credits' : 'Nieuwe campaign maken'}
                    className={`text-xs px-2.5 md:px-3 py-1 md:py-1.5 rounded-lg font-medium transition-colors ${
                      (!creditsInfo.hasCredits || !aiGenerationEnabled)
                        ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                        : 'bg-[#FF6B35] text-white hover:bg-[#FF5520]'
                    }`}
                  >
                    Nieuw +
                  </button>
                )}
              </div>
              <div className="text-center">
                <p className="text-base text-gray-700">{stats.pendingCampaigns} ingeplande campagnes</p>
              </div>
            </div>
          </Link>

          {/* Social Media Card */}
          <Link
            to="/social-media/posts"
            className="bg-white rounded-xl p-4 md:p-6 border-2 border-gray-300 shadow-sm hover:shadow-md transition-all group"
          >
            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 md:w-10 md:h-10 rounded-lg bg-gray-100 flex items-center justify-center text-gray-600">
                    <Share2 className="w-4 h-4 md:w-5 md:h-5" />
                  </div>
                  <h3 className="font-roca text-base md:text-lg font-semibold text-[#231F20] group-hover:text-pink-600 transition-colors">
                    Socials
                  </h3>
                </div>
                {!isPreviewMode && (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      if (!creditsInfo.hasCredits || !aiGenerationEnabled) return;
                      navigate('/social-media/create');
                    }}
                    disabled={!creditsInfo.hasCredits || !aiGenerationEnabled}
                    title={!aiGenerationEnabled ? 'AI generatie is tijdelijk uitgeschakeld' : !creditsInfo.hasCredits ? 'Onvoldoende credits' : 'Nieuwe social post maken'}
                    className={`text-xs px-2.5 md:px-3 py-1 md:py-1.5 rounded-lg font-medium transition-colors ${
                      (!creditsInfo.hasCredits || !aiGenerationEnabled)
                        ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                        : 'bg-[#FF6B35] text-white hover:bg-[#FF5520]'
                    }`}
                  >
                    Nieuw +
                  </button>
                )}
              </div>
              <div className="text-center">
                <p className="text-base text-gray-700">{stats.pendingSocialPosts} ingeplande posts</p>
              </div>
            </div>
          </Link>
        </div>

        {/* 4 Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-gray-600">Views (Maand)</p>
              <TrendingUp className="w-5 h-5 text-gray-400" />
            </div>
            <p className="text-xl font-bold text-[#231F20]">{stats.viewsThisMonth}</p>
          </div>

          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-gray-600">Views (Week)</p>
              <Eye className="w-5 h-5 text-gray-400" />
            </div>
            <p className="text-xl font-bold text-[#231F20]">{stats.viewsThisWeek}</p>
          </div>

          <Link
            to="/mailinglist"
            className="bg-white rounded-xl p-4 border border-gray-200 hover:border-[#FF6B35] transition-colors group"
          >
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-gray-600 group-hover:text-[#FF6B35]">Mailadressen</p>
              <Users className="w-5 h-5 text-gray-400 group-hover:text-[#FF6B35] transition-colors" />
            </div>
            <p className="text-xl font-bold text-[#231F20] group-hover:text-[#FF6B35] transition-colors">{stats.emailSubscribers}</p>
          </Link>

          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-gray-600">Volgende</p>
              <Clock className="w-5 h-5 text-gray-400" />
            </div>
            <p className="text-sm font-semibold text-[#231F20]">
              {displayUser?.next_blog_scheduled_at ? (
                new Date(displayUser.next_blog_scheduled_at).toLocaleDateString('nl-NL', {
                  day: 'numeric',
                  month: 'short',
                  hour: '2-digit',
                  minute: '2-digit'
                })
              ) : (
                <span className="text-gray-500">Niet gepland</span>
              )}
            </p>
          </div>
        </div>

        {/* Recent Items van alle 3 secties */}
        <div className="space-y-6">
          {/* Recent Blogs - Full Width */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-roca text-lg font-semibold text-[#231F20]">Recente Blogs</h2>
              <Link
                to="/my-blogs"
                className="text-sm font-medium text-gray-600 hover:text-[#231F20] transition-colors"
              >
                Alles →
              </Link>
            </div>

            {loadingData ? (
              <div className="text-center py-8">
                <div className="w-6 h-6 border-2 border-gray-300 border-t-transparent rounded-full animate-spin mx-auto" />
              </div>
            ) : recentBlogs.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <p className="text-sm text-gray-500">Nog geen blogs</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {recentBlogs.map((blog) => (
                  <a
                    key={blog.id}
                    href={blogUrlConfig ? generateBlogUrl(blog.slug, blogUrlConfig, blog.wp_post_url) : '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block rounded-lg hover:shadow-sm transition-all border border-gray-100 overflow-hidden group"
                  >
                    {blog.featured_image_url && (
                      <div className="w-full h-32 overflow-hidden">
                        <img
                          src={blog.featured_image_url}
                          alt={blog.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                    )}
                    <div className="p-3">
                      <h3 className="font-medium text-[#231F20] text-sm mb-2 line-clamp-2 group-hover:text-indigo-600 transition-colors">
                        {blog.title}
                      </h3>
                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <span>{new Date(blog.published_at).toLocaleDateString('nl-NL', {
                          day: 'numeric',
                          month: 'short'
                        })}</span>
                        <span className="flex items-center gap-1">
                          <Eye className="w-3 h-3" />
                          {blog.view_count || 0}
                        </span>
                      </div>
                    </div>
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Grid for Campaigns and Socials - Side by Side on Large Screens */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Recent Campaigns */}
            <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-roca text-lg font-semibold text-[#231F20]">Recente campagnes</h2>
              <Link
                to="/campaigns"
                className="text-sm font-medium text-gray-600 hover:text-[#231F20] transition-colors"
              >
                Alles →
              </Link>
            </div>

            {loadingData ? (
              <div className="text-center py-8">
                <div className="w-6 h-6 border-2 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto" />
              </div>
            ) : recentCampaigns.length === 0 ? (
              <div className="text-center py-8">
                <Mail className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <p className="text-sm text-gray-500">Nog geen campaigns</p>
              </div>
            ) : (
              <div className="space-y-3">
                {recentCampaigns.map((campaign) => {
                  const statusStyles = {
                    draft: 'bg-gray-100 text-gray-700',
                    scheduled: 'bg-blue-100 text-blue-700',
                    sending: 'bg-yellow-100 text-yellow-700',
                    completed: 'bg-green-100 text-green-700',
                    failed: 'bg-red-100 text-red-700',
                  };

                  const statusLabels = {
                    draft: 'Concept',
                    scheduled: 'Gepland',
                    sending: 'Bezig',
                    completed: 'Verzonden',
                    failed: 'Mislukt',
                  };

                  return (
                    <Link
                      key={campaign.id}
                      to={`/campaigns/${campaign.id}`}
                      className="block p-3 rounded-lg hover:bg-gray-50 transition-colors border border-gray-100"
                    >
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <h3 className="font-medium text-[#231F20] text-sm line-clamp-1 flex-1 hover:text-purple-600 transition-colors">
                          {campaign.subject}
                        </h3>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusStyles[campaign.status]}`}>
                          {statusLabels[campaign.status]}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500">
                        {campaign.sent_at
                          ? new Date(campaign.sent_at).toLocaleDateString('nl-NL', {
                              day: 'numeric',
                              month: 'short'
                            })
                          : campaign.scheduled_at
                          ? new Date(campaign.scheduled_at).toLocaleDateString('nl-NL', {
                              day: 'numeric',
                              month: 'short',
                              hour: '2-digit',
                              minute: '2-digit'
                            })
                          : new Date(campaign.id).toLocaleDateString('nl-NL', {
                              day: 'numeric',
                              month: 'short'
                            })}
                      </p>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>

          {/* Recent Social Posts */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-roca text-lg font-semibold text-[#231F20]">Recente Socials</h2>
              <Link
                to="/social-media/posts"
                className="text-sm font-medium text-gray-600 hover:text-[#231F20] transition-colors"
              >
                Alles →
              </Link>
            </div>

            {loadingData ? (
              <div className="text-center py-8">
                <div className="w-6 h-6 border-2 border-gray-300 border-t-transparent rounded-full animate-spin mx-auto" />
              </div>
            ) : recentSocialPosts.length === 0 ? (
              <div className="text-center py-8">
                <Share2 className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <p className="text-sm text-gray-500">Nog geen posts</p>
              </div>
            ) : (
              <div className="space-y-3">
                {recentSocialPosts.map((post) => {
                  const statusStyles = {
                    draft: 'bg-gray-100 text-gray-700',
                    scheduled: 'bg-blue-100 text-blue-700',
                    published: 'bg-green-100 text-green-700',
                    failed: 'bg-red-100 text-red-700',
                  };

                  const statusLabels = {
                    draft: 'Concept',
                    scheduled: 'Gepland',
                    published: 'Gepost',
                    failed: 'Mislukt',
                  };

                  return (
                    <Link
                      key={post.id}
                      to={`/social-media/edit/${post.id}`}
                      className="block p-3 rounded-lg hover:bg-gray-50 transition-colors border border-gray-100"
                    >
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <p className="text-sm text-[#231F20] line-clamp-2 flex-1 hover:text-pink-600 transition-colors">
                          {post.post_content}
                        </p>
                      </div>
                      <div className="flex items-center justify-between text-xs text-gray-500 mt-2">
                        <span>
                          {post.scheduled_at
                            ? new Date(post.scheduled_at).toLocaleDateString('nl-NL', {
                                day: 'numeric',
                                month: 'short'
                              })
                            : new Date(post.created_at).toLocaleDateString('nl-NL', {
                                day: 'numeric',
                                month: 'short'
                              })}
                        </span>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full font-medium ${statusStyles[post.status as keyof typeof statusStyles]}`}>
                          {statusLabels[post.status as keyof typeof statusLabels] || post.status}
                        </span>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
          </div>
        </div>
        </>}
      </main>
      </div>
      {showWalkthrough && <WalkthroughOverlay onClose={() => setShowWalkthrough(false)} />}
    </div>
  );
}
